/**
 * Modelo de datos de DuoFit (design.md, sección 7).
 *
 * CONVENCIONES (de Control de Negocio de Comida):
 * - Tablas en inglés; campos en español sin tildes.
 * - Ids `prefijo_uuid` con nuevoId(); los catálogos base usan ids fijos.
 * - Unidades canónicas: kg, cm, g, kcal, segundos y metros, con precisión
 *   completa. Lo que el usuario escribió va aparte, en `Capturado`.
 * - Días como FechaLocal 'AAAA-MM-DD'; horas como HoraLocal 'HH:MM';
 *   instantes (creadoEn, inicio, momento…) como ISO en UTC.
 * - Un campo nuevo no indexado no necesita versión de Dexie: se declara
 *   opcional y "ausente" se lee igual que null.
 *
 * Un perfil por teléfono: todos los registros de esta base son del perfil de
 * `settings` (design.md, sección 1, "Dos usuarios, dos teléfonos").
 */

import type { FechaLocal } from '@/domain/dates';
import type { BarraYDiscos } from '@/domain/discos';
import type { HoraLocal } from '@/domain/time';
import type { Capturado, CargaCapturada, UnidadCarga, UnidadLongitud, UnidadPeso } from '@/domain/units';

export type { FechaLocal, HoraLocal, Capturado, CargaCapturada, UnidadCarga, UnidadPeso, UnidadLongitud, BarraYDiscos };

/**
 * Versión del CONTENIDO, la que viaja en los respaldos. Es distinta de las
 * versiones de Dexie (`.version(n)` en schema.ts), aunque hoy coinciden.
 * Al subirla hay que agregar su eslabón en `migrarDatos` (backup-format.ts).
 *
 * 2 (Fase 1, Nutrición): `estado` crudo o cocido en Mis alimentos y en las
 * comidas, y `favoritosBase` en los ajustes. Son campos nuevos opcionales:
 * un registro del esquema 1 ya es válido en el 2.
 */
export const VERSION_ESQUEMA = 3;

// ---------------------------------------------------------------------------
// Ajustes y perfil (registro único, id 1)
// ---------------------------------------------------------------------------

export type NivelActividad = 'sedentario' | 'ligero' | 'moderado' | 'alto' | 'muy_alto';
export type Sexo = 'mujer' | 'hombre';
export type Tema = 'sistema' | 'claro' | 'oscuro';
export type MetodoAyuno = 'comidas' | 'temporizador';

export const NIVELES_ACTIVIDAD: readonly NivelActividad[] = [
  'sedentario',
  'ligero',
  'moderado',
  'alto',
  'muy_alto',
];

/** Nombre y descripción en palabras de cada nivel (Req. 4.4). */
export const DESCRIPCION_ACTIVIDAD: Readonly<
  Record<NivelActividad, { nombre: string; descripcion: string }>
> = {
  sedentario: { nombre: 'Sedentario', descripcion: 'Trabajo sentado y casi sin ejercicio' },
  ligero: { nombre: 'Ligero', descripcion: 'Ejercicio ligero 1 a 3 días por semana' },
  moderado: { nombre: 'Moderado', descripcion: 'Ejercicio moderado 3 a 5 días por semana' },
  alto: { nombre: 'Alto', descripcion: 'Ejercicio intenso 6 a 7 días por semana' },
  muy_alto: {
    nombre: 'Muy alto',
    descripcion: 'Entrenamiento intenso diario o trabajo muy físico',
  },
};

/** Metas manuales que aplicaron en un periodo ya cerrado (esquema 2). */
export interface PeriodoMetasManuales {
  /** null = desde siempre. */
  desde: FechaLocal | null;
  hasta: FechaLocal;
  kcal: number | null;
  proteinaG: number | null;
  carbohidratosG: number | null;
  grasaG: number | null;
  ayunoHoras: number | null;
}

export const ETIQUETAS_SEXO: Readonly<Record<Sexo, string>> = {
  mujer: 'Mujer',
  hombre: 'Hombre',
};

export const ETIQUETAS_TEMA: Readonly<Record<Tema, string>> = {
  sistema: 'Según el sistema',
  claro: 'Claro',
  oscuro: 'Oscuro',
};

/** Datos para comprobar la contraseña de cifrado sin guardarla (Req. 6.19). */
export interface CifradoRespaldo {
  /** Sal del verificador, en base64. */
  sal: string;
  /** Valor derivado de la contraseña, en base64. Nunca la contraseña. */
  verificador: string;
  iteraciones: number;
  activadoEn: string;
}

export interface Settings {
  id: 1;
  schemaVersion: number;
  /** 'per_<uuid>'. Se crea una sola vez y viaja en los respaldos (Req. 4.7). */
  perfilId: string;

  // Perfil: todo opcional (Req. 4.2)
  nombre: string | null;
  sexo: Sexo | null;
  fechaNacimiento: FechaLocal | null;
  estaturaCm: number | null;
  /** Lo que se escribió al capturar la estatura (Req. 5.4). */
  estaturaCapturada: Capturado | null;
  nivelActividad: NivelActividad | null;
  primerosPasosHechos: boolean;

  // Unidades y apariencia
  unidadPeso: UnidadPeso;
  unidadLongitud: UnidadLongitud;
  tema: Tema;

  // Gym (Fase 2)
  descansoPredeterminadoSeg: number;
  incrementoPredeterminadoKg: number;
  pantallaEncendida: boolean;
  /**
   * Seguir avisando el fin del descanso con la pantalla bloqueada (0.12.0).
   * Reproduce audio de verdad para que iOS no suspenda la app, así que pausa
   * la música y suena aunque el teléfono esté en silencio. Ausente = apagado.
   */
  avisoConPantallaBloqueada?: boolean;
  /**
   * Avisar el fin del descanso aunque la app esté CERRADA (0.15.0). Necesita
   * Web Push, o sea el servidor de avisos: del teléfono salen la suscripción
   * que da Apple y el instante del aviso, nada más. Pide la app instalada en
   * la pantalla de inicio y el permiso de avisos. Ausente = apagado.
   */
  avisoConAppCerrada?: boolean;
  /**
   * De quién son las rutinas precargadas de este teléfono (0.3.3). Se elige
   * con un toque en Gym: no depende de cómo se escribió el nombre del
   * perfil. Ausente o null = sin elegir.
   */
  rutinasDe?: PersonaRutinas | null;
  /** La modalidad que muestra Gym (0.3.4). Ausente = gimnasio. Solo filtra: no cambia datos. */
  modalidadGym?: Modalidad;
  /**
   * La barra y los discos de la calculadora (Req. 26.3, 26.4; 0.8.1). Se
   * guardan en la unidad en que están grabados, no en kg: un disco de 45 lb
   * no es "20.411 kg". Ausente o null = los predeterminados de la unidad
   * preferida.
   */
  barraYDiscos?: BarraYDiscos | null;

  // Cuerpo (Fase 3)
  basculaTendenciaId: string | null;
  ultimaBasculaId: string | null;
  medidasOcultas: TipoMedida[];

  // Comida, ayuno y metas sin plan (Fases 1 y 4)
  metodoAyuno: MetodoAyuno;
  metaKcalManual: number | null;
  metaProteinaManualG: number | null;
  metaCarbohidratosManualG: number | null;
  metaGrasaManualG: number | null;
  metaAyunoManualHoras: number | null;
  /**
   * Esquema 2: desde qué día aplican las metas manuales de arriba. null =
   * desde siempre (lo que trae un respaldo del esquema 1).
   */
  metasManualesDesde?: FechaLocal | null;
  /**
   * Esquema 2: las metas manuales que hubo antes, con sus fechas. Cambiar las
   * metas aplica desde hoy y los días anteriores conservan las suyas.
   */
  metasManualesAnteriores?: PeriodoMetasManuales[];
  /** Ids de alimentos de la base marcados como favoritos (esquema 2). */
  favoritosBase: string[];
  /**
   * Comidas habituales (0.3.5): plantillas de desayuno o comida que se
   * proponen en sus días y se registran solo al confirmarlas. Ausente = [].
   */
  comidasHabituales?: ComidaHabitual[];
  /**
   * La última etapa nueva cuyo aviso se marcó como visto en Hoy (0.3.6;
   * Req. 21.8), como "plan:ciclo:índice". Ausente o null = ninguna.
   */
  avisoEtapaVisto?: string | null;
  /**
   * El último aviso de ayuno de más de 24 h que se cerró (Req. 23.9; 0.6.2),
   * como "dia:2026-10-28" o "curso:2026-10-27". Así se muestra una sola vez.
   * Ausente o null = ninguno.
   */
  avisoAyunoLargoVisto?: string | null;
  /**
   * Instante de la última ingesta cuando se cerró la ventana de alimentación
   * (0.16.0; Req. 23.14). Desde ahí cuenta el ayuno siguiente. Volver a comer
   * después vuelve a abrir la ventana sola, porque esa ingesta es posterior.
   * Ausente o null = la ventana nunca se ha cerrado.
   */
  ventanaCerradaEn?: string | null;

  // Respaldo
  ultimoRespaldoEn: string | null;
  diasAvisoRespaldo: number;
  almacenamientoPersistente: boolean | null;
  /** null = cifrado desactivado, que es lo predeterminado (Req. 6.18). */
  cifradoRespaldo: CifradoRespaldo | null;

  creadoEn: string;
}

// ---------------------------------------------------------------------------
// Gym (Fase 2)
// ---------------------------------------------------------------------------

export type TipoEjercicio = 'peso_reps' | 'reps' | 'tiempo' | 'distancia';

/** Las dos personas con rutinas precargadas (design.md, 11.3). */
export type PersonaRutinas = 'janeth' | 'fernando';

/**
 * Dónde se entrena (0.3.4): cada modalidad tiene sus rutinas y su
 * progresión. En rutinas y entrenamientos, ausente = gimnasio: todo lo
 * anterior a la 0.3.4 se hizo en el gimnasio.
 */
export type Modalidad = 'gimnasio' | 'casa';
export type GrupoMuscular =
  | 'pecho'
  | 'espalda'
  | 'hombros'
  | 'biceps'
  | 'triceps'
  | 'antebrazos'
  | 'abdomen'
  | 'gluteos'
  | 'cuadriceps'
  | 'femorales'
  | 'pantorrillas'
  | 'aductores'
  | 'cuerpo_completo'
  | 'cardio';
export type Equipo =
  | 'barra'
  | 'mancuernas'
  | 'maquina'
  | 'polea'
  | 'peso_corporal'
  | 'kettlebell'
  | 'banda'
  | 'smith'
  | 'otro';
export type TipoSerie = 'calentamiento' | 'normal' | 'dropset' | 'fallo';

export interface Exercise {
  /** 'ejb_<slug>' en el catálogo base; 'ej_<uuid>' en los propios. */
  id: string;
  nombre: string;
  grupoMuscular: GrupoMuscular;
  equipo: Equipo;
  tipo: TipoEjercicio;
  esBase: boolean;
  /**
   * La unidad con que se captura su carga y que la app recuerda (Req. 9.5):
   * kg, lb o 'placa' (el número que marca la máquina). null = la preferida.
   */
  unidadCaptura: UnidadCarga | null;
  /** null = el incremento predeterminado. */
  incrementoKg: number | null;
  notas: string | null;
  archivado: boolean;
  creadoEn: string;
  actualizadoEn: string;
}

export interface Routine {
  id: string;
  nombre: string;
  notas: string | null;
  orden: number;
  /**
   * Días de la semana en que toca (0 = domingo … 6 = sábado). Solo sirve
   * para proponerla: la app no la da por hecha ni pasa a la siguiente sola.
   * Ausente = sin días.
   */
  dias?: number[];
  /**
   * La rutina precargada de la que salió ('janeth.lunes', 'fernando.a'), para
   * no volver a crearla aunque se le cambie el nombre (0.3.3). Ausente o
   * null = propia, o creada antes de la 0.3.3 (esas se reconocen por nombre).
   */
  plantilla?: string | null;
  /** Gimnasio o casa (0.3.4). Ausente = gimnasio. */
  modalidad?: Modalidad;
  creadoEn: string;
  actualizadoEn: string;
}

export interface RoutineExercise {
  id: string;
  routineId: string;
  exerciseId: string;
  orden: number;
  series: number;
  /** null en ejercicios de tiempo o distancia (Req. 10.2). */
  repsMin: number | null;
  repsMax: number | null;
  /** Descanso entre series cuando se hace solo (o la biserie por separado). */
  descansoSeg: number;
  notas: string | null;
  /**
   * Biserie: los ejercicios seguidos con la misma clave (por ejemplo 'A') se
   * muestran juntos y se alternan; el descanso empieza al completar la serie
   * de todos. null o ausente = va solo.
   */
  biserie?: string | null;
  /** Descanso después de completar la serie de toda la biserie. */
  descansoBiserieSeg?: number | null;
  /** Segundos que se sostiene la posición en cada repetición, como objetivo (0.3.4). */
  pausaSeg?: number | null;
  /** La variante con que se hace, por ejemplo "inclinadas" (0.3.4). */
  variante?: string | null;
  /**
   * Repeticiones por lado (0.3.5): en cada serie se anotan las de cada lado y
   * completar los dos lados cuenta como una serie. Ausente = false.
   */
  porLado?: boolean;
}

export interface Workout {
  id: string;
  estado: 'en_curso' | 'terminado';
  /** Pasa a null si se borra la rutina (design.md, 7.9). */
  routineId: string | null;
  /** Copia del nombre de la rutina o "Entrenamiento libre". */
  nombre: string;
  fecha: FechaLocal;
  inicio: string;
  fin: string | null;
  notas: string | null;
  /** Solo en curso: fin del descanso guardado (Req. 12.5). */
  descanso: { terminaEn: string; duracionSeg: number } | null;
  /** Dónde se hizo (0.3.4), fijado al empezar. Ausente = gimnasio. */
  modalidad?: Modalidad;
  creadoEn: string;
  actualizadoEn: string;
}

export interface WorkoutExercise {
  id: string;
  workoutId: string;
  exerciseId: string;
  orden: number;
  /** Copia del objetivo de la rutina al iniciar. */
  repsMin: number | null;
  repsMax: number | null;
  descansoSeg: number;
  notas: string | null;
  /** Copia de la biserie de la rutina; null si va solo o se hace por separado. */
  biserie?: string | null;
  descansoBiserieSeg?: number | null;
  /**
   * Lo que se hizo en este entrenamiento (0.3.4): segundos de pausa en cada
   * repetición y variante. Empiezan con los de la vez pasada en la misma
   * modalidad, o con los de la rutina.
   */
  pausaSeg?: number | null;
  variante?: string | null;
  /** Copia de la rutina (0.3.5): las series se anotan por lado. */
  porLado?: boolean;
}

export interface WorkoutSet {
  id: string;
  workoutId: string;
  workoutExerciseId: string;
  /** Igual al de su workoutExercise; repetido para consultar el historial sin cruces. */
  exerciseId: string;
  orden: number;
  tipoSerie: TipoSerie;
  /** null si no se capturó o si la carga está en placas (no se convierte). */
  pesoKg: number | null;
  /** La carga tal como se escribió: kg, lb o placas. */
  pesoCapturado: CargaCapturada | null;
  reps: number | null;
  /** En los ejercicios por lado (0.3.5): las de cada lado; `reps` queda vacío. */
  repsIzq?: number | null;
  repsDer?: number | null;
  duracionSeg: number | null;
  distanciaM: number | null;
  rir: number | null;
  hecha: boolean;
  hechaEn: string | null;
}

// ---------------------------------------------------------------------------
// Cuerpo (Fase 3)
// ---------------------------------------------------------------------------

export type TipoMedida =
  | 'cintura'
  | 'cadera'
  | 'pecho'
  | 'brazo'
  | 'muslo'
  | 'pantorrilla'
  | 'cuello'
  | 'grasa';

export interface Scale {
  id: string;
  nombre: string;
  unidad: UnidadPeso;
  archivada: boolean;
  orden: number;
  creadoEn: string;
  actualizadoEn: string;
}

export interface WeighIn {
  id: string;
  fecha: FechaLocal;
  hora: HoraLocal;
  momento: string;
  pesoKg: number;
  capturado: Capturado;
  scaleId: string | null;
  nota: string | null;
  creadoEn: string;
  actualizadoEn: string;
}

export interface Measurement {
  id: string;
  fecha: FechaLocal;
  tipo: TipoMedida;
  /** cm, o % si el tipo es 'grasa'. */
  valor: number;
  /** null en 'grasa'. */
  capturado: Capturado | null;
  nota: string | null;
  creadoEn: string;
  actualizadoEn: string;
}

// ---------------------------------------------------------------------------
// Comida (Fase 1)
// ---------------------------------------------------------------------------

/**
 * Si los valores son del alimento crudo o cocido (esquema 2). null = no
 * aplica o no se sabe; un registro del esquema 1 no lo trae y se lee igual.
 */
export type EstadoCoccion = 'crudo' | 'cocido';

export interface Nutrientes {
  kcal: number;
  proteinaG: number | null;
  carbohidratosG: number | null;
  grasaG: number | null;
  fibraG: number | null;
}

export interface Porcion {
  nombre: string;
  gramos: number | null;
}

/**
 * De dónde salen los valores de un alimento propio (0.3.5): la etiqueta del
 * producto, una referencia genérica, una aproximación con la etiqueta de otra
 * presentación o una estimación (una receta, un corte sin confirmar).
 * Ausente o null = como se capturó, normalmente de su etiqueta.
 */
export type CalidadDato = 'etiqueta' | 'referencia' | 'aproximada' | 'estimada';

/** Mis alimentos. Los de la base vienen incluidos en la app, no aquí. */
export interface Food {
  id: string;
  nombre: string;
  referencia: { tipo: 'por100g' } | { tipo: 'porPorcion'; porcion: Porcion };
  /** Por la referencia: por 100 g o por la porción indicada. */
  nutrientes: Nutrientes;
  porciones: Porcion[];
  rompeAyuno: boolean;
  favorito: boolean;
  archivado: boolean;
  origenBaseId: string | null;
  /** Esquema 2. Ausente = null. */
  estado?: EstadoCoccion | null;
  /** 0.3.5. Ausente = null. */
  calidad?: CalidadDato | null;
  /** Fuente, micronutrientes de la etiqueta o cómo se calculó (0.3.5). */
  nota?: string | null;
  creadoEn: string;
  actualizadoEn: string;
}

/**
 * Foto de progreso (esquema 3, Fase 6; design.md 7.10). Aquí solo van los
 * datos: los bytes del JPEG viven en `photoBlobs`, con el mismo id. Así esta
 * tabla entra tal cual en el JSON del respaldo y su `sha256` queda cubierto
 * por el checksum del archivo, que es lo que después verifica cada foto.
 */
export interface Photo {
  id: string;
  fecha: FechaLocal;
  momento: string;
  pose: 'frente' | 'perfil' | 'espalda' | 'otra';
  tipo: 'image/jpeg';
  bytes: number;
  /** Hex del SHA-256 de los bytes del JPEG. */
  sha256: string;
  nota: string | null;
  creadoEn: string;
}

/** Los bytes de una foto. Tabla aparte y fuera del JSON a propósito. */
export interface PhotoBlob {
  id: string;
  datos: Blob;
}

/**
 * Bytes en memoria: una foto, un ZIP, un archivo cifrado.
 *
 * El parámetro de tipo hace falta. TypeScript modela `Uint8Array` sobre
 * cualquier búfer, incluido SharedArrayBuffer, y WebCrypto y Blob solo
 * aceptan los que viven en un ArrayBuffer normal. En la app nunca hay uno
 * compartido: los bytes salen de un Blob, de un ZIP o de getRandomValues.
 */
export type Bytes = Uint8Array<ArrayBuffer>;

export interface IngredientePlatillo {
  origen: 'base' | 'propio';
  alimentoId: string;
  cantidad: number;
  /** Nombre de la porción o 'g'. */
  porcion: string;
}

export interface Dish {
  id: string;
  nombre: string;
  raciones: number;
  ingredientes: IngredientePlatillo[];
  /**
   * Lo que pesa todo el platillo ya cocinado, en gramos. Con él se puede
   * registrar por gramos además de por ración. Ausente = null.
   */
  pesoFinalG?: number | null;
  favorito: boolean;
  archivado: boolean;
  creadoEn: string;
  actualizadoEn: string;
}

export type TiempoComida = 'desayuno' | 'comida' | 'cena' | 'colacion';

export interface MealEntry {
  id: string;
  fecha: FechaLocal;
  hora: HoraLocal;
  momento: string;
  tiempo: TiempoComida;
  origen: { tipo: 'rapido' } | { tipo: 'base' | 'propio' | 'platillo'; refId: string };
  /** Copia del nombre. */
  nombre: string;
  cantidad: number;
  porcion: string | null;
  gramos: number | null;
  /** Copia ya multiplicada por la cantidad (Req. 18.4). */
  valores: Nutrientes;
  rompeAyuno: boolean;
  /** Copia del estado del alimento al registrarlo (esquema 2). Ausente = null. */
  estado?: EstadoCoccion | null;
  /** Copia de la calidad del dato del alimento propio (0.3.5). Ausente = null. */
  calidad?: CalidadDato | null;
  /** La comida habitual con que se registró, para no registrarla dos veces el mismo día (0.3.5). */
  habitual?: string | null;
  nota: string | null;
  creadoEn: string;
  actualizadoEn: string;
}

/**
 * Comida habitual (0.3.5): un desayuno o una comida que se repite. Se
 * propone en sus días desde `desde` y hasta hoy, y solo se registra al
 * confirmarla, con las cantidades que se ajusten ese día. No llena días
 * hacia atrás, no se asigna a otros días y no cambia metas.
 */
export interface ComidaHabitual {
  /** Fija en las precargadas ('janeth.desayuno'). */
  id: string;
  nombre: string;
  tiempo: TiempoComida;
  /** 0 = domingo … 6 = sábado. */
  dias: number[];
  /** Alimentos de la base o de Mis alimentos con su cantidad y porción ('g', 'ml' o el nombre). */
  items: IngredientePlatillo[];
  desde: FechaLocal;
  creadoEn: string;
  actualizadoEn: string;
}

export interface Day {
  /** Clave primaria. */
  fecha: FechaLocal;
  registroIncompleto: boolean;
  nota: string | null;
  actualizadoEn: string;
}

// ---------------------------------------------------------------------------
// Planes y mantenimiento (Fase 1) y ayuno (Fase 4)
// ---------------------------------------------------------------------------

export type TipoEtapa = 'deficit' | 'mantenimiento' | 'superavit';

export type MetaKcal =
  | { modo: 'fija'; kcal: number }
  | { modo: 'diferencia'; kcal: number }
  | { modo: 'porcentaje'; pct: number };

export interface EtapaPlantilla {
  tipo: TipoEtapa;
  /** null = sin fin. */
  semanas: number | null;
  metaKcal: MetaKcal | null;
  proteinaG: number | null;
  carbohidratosG: number | null;
  grasaG: number | null;
  ayunoHoras: number | null;
}

export interface Plan {
  id: string;
  nombre: string;
  fechaInicio: FechaLocal;
  fechaFin: FechaLocal | null;
  repetir: boolean;
  plantilla: EtapaPlantilla[];
  estado: 'activo' | 'terminado';
  notas: string | null;
  /**
   * Esquema 2: lo que este plan le cambió al que terminó al crearse. Si se
   * borra por error el mismo día, el anterior vuelve tal como estaba.
   * Ausente = no terminó ningún plan.
   */
  reemplazo?: ReemplazoDePlan | null;
  creadoEn: string;
  actualizadoEn: string;
}

export interface ReemplazoDePlan {
  planId: string;
  estadoAnterior: Plan['estado'];
  fechaFinAnterior: FechaLocal | null;
  /** Las etapas que se quitaron porque todavía no empezaban. */
  etapasBorradas: Stage[];
  /** La etapa en curso que se recortó y su fin de antes. */
  recortes: Array<{ id: string; fechaFinAnterior: FechaLocal | null }>;
}

/** Etapa con fechas concretas. */
export interface Stage {
  id: string;
  planId: string;
  ciclo: number;
  /** Posición en la plantilla. */
  indice: number;
  tipo: TipoEtapa;
  fechaInicio: FechaLocal;
  fechaFin: FechaLocal | null;
  metaKcal: MetaKcal | null;
  proteinaG: number | null;
  carbohidratosG: number | null;
  grasaG: number | null;
  ayunoHoras: number | null;
  creadoEn: string;
  actualizadoEn: string;
}

/** Gramos de cada macro al día (0.5.1). */
export interface MacrosDiarios {
  proteinaG: number;
  carbohidratosG: number;
  grasaG: number;
}

export interface Maintenance {
  id: string;
  desde: FechaLocal;
  origen: 'formula' | 'estimado' | 'manual';
  decision: 'aceptada' | 'modificada' | 'rechazada' | 'manual';
  /** Lo que propuso la app; null si es manual. */
  propuestaKcal: number | null;
  /** Lo aplicado; null si se rechazó. */
  kcal: number | null;
  /**
   * Los macros aceptados con esas kcal (0.5.1). Ausente o null = sin macros:
   * una etapa de mantenimiento sin macros propios se queda sin ellos, en
   * lugar de heredar unos de otras kcal.
   */
  macros?: MacrosDiarios | null;
  supuestos: string[];
  detalle: string;
  creadoEn: string;
}

export interface Fast {
  id: string;
  inicio: string;
  /** null = en curso (solo con temporizador). */
  fin: string | null;
  metaHoras: number | null;
  origen: 'temporizador' | 'manual' | 'comidas' | 'correccion';
  /** Cálculo con comidas al confirmar o corregir (Req. 23.11). */
  calculado: { inicio: string; fin: string } | null;
  /** Valores anteriores a cada corrección (Req. 23.11). */
  historial: Array<{ cambiadoEn: string; inicio: string; fin: string | null }>;
  nota: string | null;
  avisoLargoMostrado: boolean;
  creadoEn: string;
  actualizadoEn: string;
}

// ---------------------------------------------------------------------------
// Mapa de tablas, usado por el sistema de respaldos
// ---------------------------------------------------------------------------

export interface MapaTablas {
  settings: Settings[];
  scales: Scale[];
  exercises: Exercise[];
  routines: Routine[];
  routineExercises: RoutineExercise[];
  workouts: Workout[];
  workoutExercises: WorkoutExercise[];
  workoutSets: WorkoutSet[];
  weighIns: WeighIn[];
  measurements: Measurement[];
  foods: Food[];
  dishes: Dish[];
  mealEntries: MealEntry[];
  days: Day[];
  plans: Plan[];
  stages: Stage[];
  maintenance: Maintenance[];
  fasts: Fast[];
  /** Esquema 3: solo los datos. Los bytes viajan como archivos del ZIP (10.7). */
  photos: Photo[];
}

export type NombreTabla = keyof MapaTablas;

/**
 * Acceso dinámico al mapa de tablas.
 *
 * El respaldo recorre todas las tablas por nombre, y cada una tiene un tipo
 * distinto. La conversión se concentra en estos dos ayudantes en lugar de
 * repetirse en cada punto de uso (patrón de Control de Negocio de Comida).
 */
export function escribirTabla(
  mapa: MapaTablas,
  tabla: NombreTabla,
  registros: readonly unknown[],
): void {
  (mapa as unknown as Record<NombreTabla, readonly unknown[]>)[tabla] = registros;
}

/** Lee una tabla como registros genéricos, para inspeccionar campos por nombre. */
export function leerTabla(
  mapa: MapaTablas,
  tabla: NombreTabla,
): ReadonlyArray<Record<string, unknown>> {
  const vista = mapa as unknown as Record<
    NombreTabla,
    ReadonlyArray<Record<string, unknown>> | undefined
  >;
  return vista[tabla] ?? [];
}

/**
 * Orden de las tablas para exportar e importar: las padre antes que las hijas,
 * para revisar la integridad referencial en un solo recorrido (design.md, 10.1).
 */
export const TABLAS: readonly NombreTabla[] = [
  'settings',
  'scales',
  'exercises',
  'routines',
  'routineExercises',
  'workouts',
  'workoutExercises',
  'workoutSets',
  'weighIns',
  'measurements',
  'foods',
  'dishes',
  'mealEntries',
  'days',
  'plans',
  'stages',
  'maintenance',
  'fasts',
  // Esquema 3 (Fase 6): solo los datos de cada foto. Los bytes viven en
  // `photoBlobs`, que NO entra al JSON: viajan como archivos del ZIP (10.7)
  'photos',
];

/** Nombre de cada tabla en la vista previa de un respaldo (Req. 6.5). */
export const ETIQUETAS_TABLA: Readonly<Record<NombreTabla, string>> = {
  settings: 'Ajustes y perfil',
  scales: 'Básculas',
  exercises: 'Ejercicios',
  routines: 'Rutinas',
  routineExercises: 'Ejercicios de rutinas',
  workouts: 'Entrenamientos',
  workoutExercises: 'Ejercicios de entrenamientos',
  workoutSets: 'Series',
  weighIns: 'Pesajes',
  measurements: 'Medidas',
  foods: 'Mis alimentos',
  dishes: 'Platillos',
  mealEntries: 'Comidas',
  days: 'Días',
  plans: 'Planes',
  stages: 'Etapas',
  maintenance: 'Mantenimiento',
  fasts: 'Ayunos',
  photos: 'Fotos de progreso',
};
